import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());


  #wheelDistance = 0;
  #wheelLockedUntil = 0;
  #suppressClickUntil = 0;
  /** @type {{ x: number, y: number, time: number } | null} */
  #swipeStart = null;

  /** @param {number} direction */
  #movePhoto = (direction) => {
    const { dialog, media } = this.refs;
    if (!media.length) return;
    const bounds = dialog.getBoundingClientRect();
    const current = media.reduce((nearest, item, index) =>
      Math.abs(item.getBoundingClientRect().top - bounds.top) <
      Math.abs(media[nearest].getBoundingClientRect().top - bounds.top) ? index : nearest, 0);
    const next = Math.max(0, Math.min(media.length - 1, current + direction));
    this.selectThumbnail(next, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.refs.dialog.open || event.ctrlKey || event.target.closest('.dialog-thumbnails-list')) return;
    event.preventDefault();
    if (performance.now() < this.#wheelLockedUntil) return;
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (this.#wheelDistance * delta < 0) this.#wheelDistance = 0;
    this.#wheelDistance += delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.refs.dialog.clientHeight : 1);
    if (Math.abs(this.#wheelDistance) < 24) return;
    this.#movePhoto(Math.sign(this.#wheelDistance));
    this.#wheelDistance = 0;
    this.#wheelLockedUntil = performance.now() + 550;
  };

  /** @param {TouchEvent} event */
  #handleSwipeStart = (event) => {
    this.#swipeStart = null;
    if (!(event.target instanceof Element)) return;
    const wrapper = event.target.closest('drag-zoom-wrapper');
    if (!this.refs.dialog.open || event.touches.length !== 1 || !event.target.closest('.dialog-zoomed-gallery > .product-media-container')) return;
    const scale = (wrapper ? Number.parseFloat(wrapper.style.getPropertyValue('--drag-zoom-scale')) : 1) || 1;
    if (scale > 1.01) return;
    const touch = event.touches[0];
    this.#swipeStart = { x: touch.clientX, y: touch.clientY, time: performance.now() };
  };

  /** @param {TouchEvent} event */
  #handleSwipeEnd = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!(event.target instanceof Element)) return;
    if (!start || event.touches.length || !event.changedTouches.length) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const distance = Math.abs(dx) >= Math.abs(dy) ? dx : dy;
    const wrapper = event.target.closest('drag-zoom-wrapper');
    const scale = wrapper ? Number.parseFloat(wrapper.style.getPropertyValue('--drag-zoom-scale')) || 1 : 1;
    if (scale > 1.01 || Math.abs(distance) < 45 || performance.now() - start.time > 1200) return;
    event.preventDefault();
    this.#suppressClickUntil = performance.now() + 500;
    this.#movePhoto(distance < 0 ? 1 : -1);
  };

  /** @param {MouseEvent} event */
  #handleSwipeClick = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    event.preventDefault();
    event.stopPropagation();
  };

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('close', this.#onDialogClose);
    this.refs.dialog.addEventListener('wheel', this.#handleWheel, { passive: false });
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { capture: true, passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { capture: true, passive: false });
    this.refs.dialog.addEventListener('click', this.#handleSwipeClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('close', this.#onDialogClose);
    this.refs.dialog.removeEventListener('wheel', this.#handleWheel);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSwipeStart, true);
    this.refs.dialog.removeEventListener('touchend', this.#handleSwipeEnd, true);
    this.refs.dialog.removeEventListener('click', this.#handleSwipeClick, true);
  }

  /**
   * Announces the close on `window` however the dialog closed, including the
   * browser closing it on a close request such as the Android Back button.
   */
  #onDialogClose = () => {
    window.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (['ArrowRight', 'ArrowDown', 'PageDown', 'ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
      event.preventDefault();
      this.#movePhoto(['ArrowRight', 'ArrowDown', 'PageDown'].includes(event.key) ? 1 : -1);
      return;
    }
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}

